import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Filter, MessageSquare } from "lucide-react";
import AdminActions from "@/components/AdminActions";
import { fontClass } from "@/lib/fonts";
import CopyPromptButton from "@/components/CopyPromptButton";
import Seo from "@/components/Seo";

const cats = ["All", "DesignAI", "Copywriting", "Video", "Branding"];

export default function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [cat, setCat] = useState("All");

  const load = useCallback(() => {
    api.get("/prompts").then(r => setPrompts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = cat === "All" ? prompts : prompts.filter(p => p.category === cat);

  return (
    <div className="min-h-screen pt-28">
      <Seo title="Prompt Library" description="Ready-to-use AI prompts for design, marketing, copywriting, video and branding — copy with one tap." path="/prompts" />
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="overline-label mb-4 flex items-center gap-2"><MessageSquare size={13} /> Prompt Library</div>
          <h1 className="font-display font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-white">
            Prompts from real work.<br />
            <span className="font-bn-display italic font-normal text-gradient">Copy it, ship it.</span>
          </h1>
          <p className="text-slate-200 mt-6 max-w-2xl leading-relaxed">
            Each prompt below has shipped paid work. No theory, no recycled lists — every entry is something we'd open and run today.
          </p>

          <div className="mt-6"><AdminActions kind="prompts" onChange={load} /></div>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-200" />
            {cats.map(c => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`prompt-filter-${c.toLowerCase()}`}
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                  cat === c
                    ? "bg-white text-[#04121A]"
                    : "bg-white/10 text-slate-200 hover:bg-white/20"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/prompts/${p.slug}`}
              className="card-void overflow-hidden group flex flex-col h-full"
              data-testid={`prompt-tile-${p.slug}`}
            >
              <div className="aspect-[16/10] overflow-hidden relative bg-white/10">
                <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                <div className="absolute top-3 right-3">
                  <AdminActions kind="prompts" item={p} onChange={load} mode="row" />
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <span className="chip self-start mb-3">{p.category}</span>
                <h3 className={`${fontClass(p.title_font)} font-bold text-lg leading-snug text-white group-hover:text-orange-300 transition-colors`}>{p.title}</h3>
                <p className="text-sm text-slate-200 mt-2 line-clamp-2 flex-1 leading-relaxed">{p.summary}</p>
                <div className="mt-4 pt-4 border-t border-white/[0.07] flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-orange-400 text-xs font-bold uppercase tracking-[0.1em] group-hover:gap-2.5 transition-all">
                    Open Prompt <ArrowUpRight size={12} />
                  </span>
                  <CopyPromptButton text={p.prompt_text} testId={`copy-prompt-tile-${p.slug}`} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
